import type { PayloadAction } from '@reduxjs/toolkit'

import { createSlice } from '@reduxjs/toolkit'

import type {ILabelEvent, IWeightEvent} from "../../types/events";

export interface IPaginatedListState<T> {
  rows: T[];
  page: number;
  rowsPerPage: number;
  totalCount: number | null;
  hasNext: boolean;
}

export interface EventsState {
  isLoading: boolean,
  hasError: boolean,
  error: string,
  label: IPaginatedListState<ILabelEvent>,
  weight: IPaginatedListState<IWeightEvent>,
}

const initialListState = <T>(): IPaginatedListState<T> => ({
  rows: [],
  page: 0,
  rowsPerPage: 25,
  totalCount: null,
  hasNext: false,
});

const initialState: EventsState = {
  isLoading: false,
  hasError: false,
  error: '',
  label: initialListState<ILabelEvent>(),
  weight: initialListState<IWeightEvent>(),
}

type SetLabelEventsPagePayload = {
  rows: ILabelEvent[];
  page: number;
  hasNext: boolean;
  totalCount?: number;
};

type SetWeightEventsPagePayload = {
  rows: IWeightEvent[];
  page: number;
  hasNext: boolean;
  totalCount?: number;
};

export const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
    },
    hasError: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.hasError = true;
      state.error = action.payload;
    },
    stopLoading: (state) => {
      state.isLoading = false;
    },
    setLabelEventsPage(state, action: PayloadAction<SetLabelEventsPagePayload>) {
      state.label.rows = action.payload.rows;
      state.label.page = action.payload.page;
      state.label.hasNext = action.payload.hasNext;
      if (action.payload.totalCount !== undefined) {
        state.label.totalCount = action.payload.totalCount;
      }
      state.isLoading = false;
    },
    setLabelEventsTotalCount(state, action: PayloadAction<number>) {
      state.label.totalCount = action.payload;
    },
    setLabelEventsRowsPerPage(state, action: PayloadAction<number>) {
      state.label.rowsPerPage = action.payload;
    },
    clearLabelEvents: (state) => {
      state.label.rows = [];
      state.label.page = 0;
      state.label.hasNext = false;
      state.label.totalCount = null;
      state.isLoading = false;
    },
    setWeightEventsPage(state, action: PayloadAction<SetWeightEventsPagePayload>) {
      state.weight.rows = action.payload.rows;
      state.weight.page = action.payload.page;
      state.weight.hasNext = action.payload.hasNext;
      if (action.payload.totalCount !== undefined) {
        state.weight.totalCount = action.payload.totalCount;
      }
      state.isLoading = false;
    },
    setWeightEventsTotalCount(state, action: PayloadAction<number>) {
      state.weight.totalCount = action.payload;
    },
    setWeightEventsRowsPerPage(state, action: PayloadAction<number>) {
      state.weight.rowsPerPage = action.payload;
    },
    clearWeightEvents: (state) => {
      state.weight.rows = [];
      state.weight.page = 0;
      state.weight.hasNext = false;
      state.weight.totalCount = null;
      state.isLoading = false;
    },
  },
})

export const {
  startLoading,
  stopLoading,
  hasError,
  setLabelEventsPage,
  setLabelEventsTotalCount,
  setLabelEventsRowsPerPage,
  clearLabelEvents,
  setWeightEventsPage,
  setWeightEventsTotalCount,
  setWeightEventsRowsPerPage,
  clearWeightEvents,
} = eventsSlice.actions

export default eventsSlice.reducer
